import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAndroid, isIOS, tryOpenExternalBrowser } from '../utils/inAppBrowser';

export default function InAppBrowserWarning({ appName }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const android = isAndroid();
  const ios = isIOS();
  const displayName = appName || t('inAppBrowser.genericAppName');

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // clipboard blocked — user will need to copy the URL manually
    }
  }

  return (
    <div className="min-h-screen t-bg-page flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-5xl mb-4" aria-hidden="true">🌐</div>
          <h1 className="text-2xl font-bold t-text mb-3">{t('inAppBrowser.title')}</h1>
          <p className="t-text-muted text-sm leading-relaxed">
            {appName
              ? t('inAppBrowser.body', { app: displayName })
              : t('inAppBrowser.bodyGeneric')}
          </p>
        </div>

        <div className="t-bg-surface t-border border rounded-2xl p-6 space-y-4">
          {android ? (
            <>
              <button
                type="button"
                onClick={tryOpenExternalBrowser}
                className="w-full t-btn-primary font-medium rounded-xl px-4 py-3 transition-colors t-focus-ring"
              >
                {t('inAppBrowser.openInBrowser')}
              </button>
              <p className="text-xs t-text-muted text-center">
                {t('inAppBrowser.androidHint')}
              </p>
            </>
          ) : ios ? (
            <>
              <p className="text-sm font-semibold t-text">{t('inAppBrowser.iosInstructions')}</p>
              <ol className="text-sm t-text-muted space-y-2 list-decimal list-inside">
                <li>{t('inAppBrowser.iosStep1')}</li>
                <li>{t('inAppBrowser.iosStep2')}</li>
              </ol>
              <div className="pt-2 border-t t-border">
                <p className="text-xs t-text-muted mb-3">{t('inAppBrowser.orCopyLink')}</p>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="w-full t-btn-primary font-medium rounded-xl px-4 py-3 transition-colors t-focus-ring"
                >
                  {copied ? t('inAppBrowser.linkCopied') : t('inAppBrowser.copyLink')}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm t-text-muted">{t('inAppBrowser.desktopHint')}</p>
              <button
                type="button"
                onClick={handleCopyLink}
                className="w-full t-btn-primary font-medium rounded-xl px-4 py-3 transition-colors t-focus-ring"
              >
                {copied ? t('inAppBrowser.linkCopied') : t('inAppBrowser.copyLink')}
              </button>
            </>
          )}
        </div>

        <p className="mt-4 text-center text-xs t-text-disabled leading-relaxed">
          {t('inAppBrowser.whyNote')}
        </p>
      </div>
    </div>
  );
}
